import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import * as style from "@COMPONENTS/style/detailpackage/localinformation";
import {SelectBox, SelectLabel, SelectOptions} from "@FRAMEWORK/ui/desktop/select";
import {TableContainer} from "@FRAMEWORK/ui/desktop/table";
import {SubTitle} from "@FRAMEWORK/ui/desktop/text";
import useEventLocal, {EventCity, EventExchange} from "@QUERIES/prdt/event/useEventLocal";
import dayjs from "dayjs";
import React, {useEffect, useId, useMemo, useState} from "react";

const daykor = ["일", "월", "화", "수", "목", "금", "토"];
const DetailLocalInformationComponent = ({evCd}: {evCd: string}) => {
	const useLocal = useEventLocal(evCd);
	const local = useLocal.data?.body;
	const cityInfo = local?.cityInfo || [];
	const exchangeInfo = local?.exchangeInfo || [];
	const id = useId();
	const [showTime, setShowTime] = useState(false);
	const [currentTime, setCurrentTime] = useState("");
	const [showExchange, setShowExchange] = useState(false);
	const [currentExchange, setCurrentExchange] = useState("");

	// 현재시간
	const now = useMemo(() => {
		if (!local) return null;
		const fullDate = dayjs(local.nowDate);
		const date = fullDate.format("YYYY.MM.DD");
		const HHmm = fullDate.format("HH:mm");
		const dow = daykor[fullDate.day()];
		return {fullDate, date, HHmm, dow};
	}, [local]);

	// 현지시간
	const [loc, setLoc] = useState({
		date: null,
		dow: "",
		HHmm: "",
		descript: "",
	});

	// 통화/환율 데이터
	const [exchange, setExchange] = useState<EventExchange[]>([]);

	// 현지시간 수정
	const handleTimeInfo = (city: EventCity) => {
		if (!now) return;
		const {timeDiff = "0"} = city;

		const [HhStr, mmStr] = timeDiff.split(".");
		const HH = HhStr ? Number(HhStr) : 0;
		let mm = mmStr ? Number(mmStr) : 0;
		if (Number(timeDiff) < 0) mm = -Math.abs(mm);

		const descript =
			HH === 0 && mm === 0
				? "(우리나라와 같은 시간대 사용)"
				: `(한국 시간 보다 ${Math.abs(HH)}시간 ${mm !== 0 ? `${Math.abs(mm)}분 ` : ""}${
						Number(timeDiff) > 0 ? "빠름" : "느림"
				  })`;

		const fullDate = now.fullDate.add(HH, "hour").add(mm, "minute");
		const date = fullDate.format("YYYY.MM.DD");
		const HHmm = fullDate.format("HH:mm");
		const dow = daykor[fullDate.day()];

		setLoc({descript, dow, date, HHmm});
	};

	useEffect(() => {
		// 초기값 세팅
		if (cityInfo.length > 0) {
			const city = cityInfo[0]; // 첫번째 값
			handleTimeInfo(city);
			setCurrentTime(city.cityNm);
			setCurrentExchange(city.cityNm);

			const copyExchagne = exchangeInfo.filter(v => v.currCd === exchangeInfo[0].currCd); // 달러 세팅
			setExchange(copyExchagne);
		}
	}, [local]);

	// select 콤보박스 제거 이벤트
	useEffect(() => {
		if (showTime || showExchange) {
			const event = e => {
				if (document.body.contains(e.target)) {
					!e.target.closest(`#time-${CSS.escape(id)}`) && setShowTime(false);
					!e.target.closest(`#exchange-${CSS.escape(id)}`) && setShowExchange(false);
				}
			};
			setTimeout(() => window.addEventListener("click", event));
			return () => {
				window.removeEventListener("click", event);
			};
		}
	}, [showTime, showExchange]);

	// 현지시간 셀렉트 이벤트
	const handleLocalTime = (city: EventCity) => {
		handleTimeInfo(city);

		setCurrentTime(city.cityNm);
		setShowTime(!showTime);
	};

	// 통화/환율 셀렉트 이벤트
	const handleExchange = (city: EventCity) => {
		const copyExchange = exchangeInfo.filter(v => ["USD", city.currCd].includes(v.currCd));
		setExchange(copyExchange);

		setCurrentExchange(city.cityNm);
		setShowExchange(!showExchange);
	};

	return (
		<>
			{cityInfo.length > 0 && (
				<style.Wrap>
					<style.TitArea>
						<SubTitle>현지 정보</SubTitle>
						<style.DayText>
							<span>한국시간</span>
							<span>{`${now.date} (${now.dow}) ${now.HHmm}`}</span>
						</style.DayText>
					</style.TitArea>
					<TableContainer background={false}>
						<caption>현지정보 표</caption>
						<colgroup>
							<col style={{width: "10%"}} />
							<col style={{width: "10%"}} />
							<col style={{width: "80%"}} />
						</colgroup>
						<tbody>
							<tr>
								<th scope="row">현지시간</th>
								<td>
									<style.SelectWrap id={`time-${id}`}>
										<SelectBox onClick={() => setShowTime(!showTime)} tabIndex={1}>
											<SelectLabel>{currentTime}</SelectLabel>
										</SelectBox>
										{showTime && (
											<SelectOptions>
												{cityInfo.map(city => (
													<li onClick={() => handleLocalTime(city)} key={city.cityCd} value={city.currCd}>
														{city.cityNm}
													</li>
												))}
											</SelectOptions>
										)}
									</style.SelectWrap>
								</td>
								<td>
									<style.TimeInfoWrap>
										<style.TimeInfo>
											<span>{`${loc.date} (${loc.dow})`}</span>
											<style.TimeText>{loc.HHmm}</style.TimeText>
											<span>{loc.descript}</span>
										</style.TimeInfo>
									</style.TimeInfoWrap>
								</td>
							</tr>
							<tr>
								<th scope="row">통화/환율</th>
								<td>
									<style.SelectWrap id={`exchange-${id}`}>
										<SelectBox onClick={() => setShowExchange(!showExchange)} tabIndex={1}>
											<SelectLabel>{currentExchange}</SelectLabel>
										</SelectBox>
										{showExchange && (
											<SelectOptions>
												{cityInfo.map(city => (
													<li onClick={() => handleExchange(city)} value={city.currCd} key={`ex${city.cityCd}`}>
														{city.cityNm}
													</li>
												))}
											</SelectOptions>
										)}
									</style.SelectWrap>
								</td>
								<td>
									<style.DollerInfo>
										{exchange.map((ex, index) => (
											<React.Fragment key={index}>
												{index === 1 && <br />}
												<style.DollerText>{`1${ex.exchNm}(${ex.currCd}) = ${ex.exchRat}원`}</style.DollerText>
												{index === 0 && <span>({dayjs(ex.exchDt).format("YYYY.MM.DD")} 기준)</span>}
											</React.Fragment>
										))}
									</style.DollerInfo>
								</td>
							</tr>
						</tbody>
					</TableContainer>
				</style.Wrap>
			)}
		</>
	);
};

const DetailLocalInformation = (props: {evCd: string}) => (
	<ErrorBoundary logMsg="DetailLocalInformation Error">
		<DetailLocalInformationComponent {...props} />
	</ErrorBoundary>
);

export default DetailLocalInformation;
